import React, { useState, useEffect } from 'react';
import { Modal, Table, InputNumber, Button, Space, message, Card, Typography, Divider, Alert } from 'antd';
import { customerQuoteAPI, customerOrderAPI } from '../services/api.ts';
import { ProductStock, ProductStockResponse, SelectedStock, CustomerOrderCartItem } from '../types.ts';
import { formatCurrency } from '../utils/numberFormatting.ts';

const { Title, Text } = Typography;

interface ProductAvailabilityModalProps {
  visible: boolean;
  onClose: () => void;
  productId: number;
  productName: string;
  originalPrice: number;
  onStockSelected: (selectedStocks: SelectedStock[]) => void;
  orderId?: number; // Aggiunto per la prenotazione stock negli ordini cliente
}

const ProductAvailabilityModal: React.FC<ProductAvailabilityModalProps> = ({
  visible,
  onClose,
  productId,
  productName,
  originalPrice,
  onStockSelected,
  orderId,
}) => {
  const [loading, setLoading] = useState(false);
  const [stockData, setStockData] = useState<ProductStockResponse | null>(null);
  const [stocksWithDesired, setStocksWithDesired] = useState<ProductStock[]>([]);
  const [existingReservations, setExistingReservations] = useState<CustomerOrderCartItem[]>([]);

  useEffect(() => {
    if (visible && productId) {
      loadStockData();
    }
  }, [visible, productId]);

  const loadStockData = async () => {
    setLoading(true);
    try {
      const data = await customerQuoteAPI.getProductStocks(productId);
      setStockData(data);
      setStocksWithDesired(data.stocks.map((stock: ProductStock) => ({
        ...stock,
        qta_desired: 0
      })));

      // Se c'è un orderId, carica le prenotazioni esistenti per questo prodotto
      if (orderId) {
        try {
          const cartData = await customerOrderAPI.getOrderCart(orderId);
          const productReservations = cartData.cart_items.filter(
            (item: CustomerOrderCartItem) => item.product_id === productId
          );
          setExistingReservations(productReservations);
        } catch (error) {
          console.error('Error loading existing reservations:', error);
          // Non mostra errore all'utente perché le prenotazioni sono informative
        }
      }
    } catch (error) {
      message.error('Errore nel caricamento dei dati stock');
      console.error('Error loading stock data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleQuantityChange = (index: number, value: number | null) => {
    const newStocks = [...stocksWithDesired];
    newStocks[index].qta_desired = value || 0;
    setStocksWithDesired(newStocks);
  };

  const validateAndReturn = async () => {
    // Validazione estesa per supportare prenotazioni da diverse fonti
    const invalidStocks = stocksWithDesired.filter(stock => {
      if (!stock.qta_desired || stock.qta_desired <= 0) return false;
      
      // Calcola la quantità massima prenotabile da tutte le fonti
      const maxReservable = stock.qta_available + stock.qta_in_production;
      
      return stock.qta_desired > maxReservable;
    });

    if (invalidStocks.length > 0) {
      message.error('Alcune quantità desiderate superano la quantità totale disponibile (Disponibile + In Produzione)');
      return;
    }

    // Filtra solo gli stock con quantità desiderata > 0
    const selectedStocks: SelectedStock[] = stocksWithDesired
      .filter(stock => stock.qta_desired && stock.qta_desired > 0)
      .map(stock => ({
        stock_code: stock.stock_code,
        customer_order_date: stock.customer_order_date,
        qta_desired: stock.qta_desired!,
        stock_price: stock.stock_price
      }));

    // Conferma per giacenza dedicata a un altro cliente (risposta 409 del backend)
    const confirmCrossCustomer = (detail: string) => new Promise<boolean>((resolve) => {
      Modal.confirm({
        title: 'Giacenza dedicata a un altro cliente',
        content: detail,
        okText: 'Prenota comunque',
        okButtonProps: { danger: true },
        cancelText: 'Annulla',
        onOk: () => resolve(true),
        onCancel: () => resolve(false),
      });
    });

    // Se c'è un orderId, effettua la prenotazione stock
    if (orderId && selectedStocks.length > 0) {
      try {
        setLoading(true);

        // Prenota ogni stock selezionato
        for (const selectedStock of selectedStocks) {
          try {
            await customerOrderAPI.reserveStock(orderId, {
              product_id: productId,
              stock_code: selectedStock.stock_code,
              quantity_desired: selectedStock.qta_desired
            });
          } catch (error: any) {
            // 409 = merce dedicata a un altro cliente: chiedi conferma e riprova col flag
            if (error.response?.status === 409) {
              const confirmed = await confirmCrossCustomer(error.response?.data?.detail);
              if (!confirmed) {
                message.info(`Prenotazione dello stock ${selectedStock.stock_code} annullata`);
                continue;
              }
              await customerOrderAPI.reserveStock(orderId, {
                product_id: productId,
                stock_code: selectedStock.stock_code,
                quantity_desired: selectedStock.qta_desired,
                force_cross_customer: true
              });
            } else {
              throw error;
            }
          }
        }

        message.success(`Stock prenotato con successo per ${selectedStocks.length} stock selezionati`);
        
        // Ricarica i dati stock per riflettere le modifiche
        await loadStockData();
        
      } catch (error: any) {
        console.error('Errore nella prenotazione stock:', error);
        const errorMessage = error.response?.data?.detail || 'Errore nella prenotazione stock';
        message.error(errorMessage);
        return; // Non chiudere la modale se c'è un errore
      } finally {
        setLoading(false);
      }
    }

    // Per i preventivi o quando non ci sono stock da prenotare, usa la logica esistente
    onStockSelected(selectedStocks);
    onClose();
  };

  const columns = [
    {
      title: 'Codice Stock',
      dataIndex: 'stock_code',
      key: 'stock_code',
      render: (value: string | null) => value || 'da Produrre',
    },
    {
      title: 'Data Ordine',
      dataIndex: 'customer_order_date',
      key: 'customer_order_date',
      render: (value: string | null) => value ? new Date(value).toLocaleDateString() : '-',
    },
    {
      title: 'Disponibile',
      dataIndex: 'qta_available',
      key: 'qta_available',
      render: (value: number) => value.toLocaleString(),
    },
    {
      title: 'Riservato',
      dataIndex: 'qta_reserved',
      key: 'qta_reserved',
      render: (value: number) => value.toLocaleString(),
    },
    {
      title: 'In Produzione',
      dataIndex: 'qta_in_production',
      key: 'qta_in_production',
      render: (value: number) => value.toLocaleString(),
    },
    {
      title: 'Residuo',
      dataIndex: 'qta_residuo',
      key: 'qta_residuo',
      render: (value: number) => (
        <Text strong color={value > 0 ? 'green' : 'red'}>
          {value.toLocaleString()}
        </Text>
      ),
    },
    {
      title: 'Totale',
      dataIndex: 'total_quantity',
      key: 'total_quantity',
      render: (value: number) => value.toLocaleString(),
    },
    {
      title: 'Costo',
      dataIndex: 'stock_cost',
      key: 'stock_cost',
      render: (value: number) => `€ ${formatCurrency(value)}`,
    },
    {
      title: 'Vendita',
      dataIndex: 'stock_price',
      key: 'stock_price',
      render: (value: number) => `€ ${formatCurrency(value)}`,
    },
    {
      title: 'Quantità Desiderata',
      key: 'qta_desired',
      render: (record: ProductStock, _: any, index: number) => {
        // Calcola il massimo prenotabile da tutte le fonti
        const maxReservable = record.qta_available + record.qta_in_production;
        
        return (
          <div>
            <InputNumber
              value={record.qta_desired}
              onChange={(value) => handleQuantityChange(index, value)}
              min={0}
              max={maxReservable}
              style={{ width: 100 }}
              placeholder="0"
            />
            {maxReservable > record.qta_available && (
              <div style={{ fontSize: '11px', color: '#666', marginTop: '2px' }}>
                Max: {maxReservable} 
                <br />
                <span style={{ color: '#1890ff' }}>
                  (Include In Produzione)
                </span>
              </div>
            )}
          </div>
        );
      },
    },
  ];

  const renderFooter = () => {
    if (!stockData) return null;

    const { totals } = stockData;
    
    return (
      <Card size="small" style={{ marginTop: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-around', textAlign: 'center' }}>
          <div>
            <Text strong>Disponibile</Text>
            <br />
            <Text>{totals.available.toLocaleString()}</Text>
          </div>
          <div>
            <Text strong>Riservato</Text>
            <br />
            <Text>{totals.reserved.toLocaleString()}</Text>
          </div>
          <div>
            <Text strong>In Produzione</Text>
            <br />
            <Text>{totals.in_production.toLocaleString()}</Text>
          </div>
          <div>
            <Text strong>Residuo</Text>
            <br />
            <Text strong color={totals.residuo > 0 ? 'green' : 'red'}>
              {totals.residuo.toLocaleString()}
            </Text>
          </div>
          <div>
            <Text strong>TOTALE FUTURO</Text>
            <br />
            <Text strong color="blue">
              {totals.future_total.toLocaleString()}
            </Text>
          </div>
        </div>
      </Card>
    );
  };

  return (
    <Modal
      title={`Disponibilità Prodotto - ${productName}`}
      open={visible}
      onCancel={onClose}
      width={1200}
      footer={
        <Space>
          <Button onClick={onClose}>Annulla</Button>
          <Button type="primary" onClick={validateAndReturn} loading={loading}>
            {orderId ? 'Prenota Stock' : 'Torna al Preventivo'}
          </Button>
        </Space>
      }
    >
      <div style={{ marginBottom: 16 }}>
        <Text>
          <strong>Vendita originale:</strong> € {formatCurrency(originalPrice)}
        </Text>
      </div>

      {/* Sezione Prenotazioni Esistenti */}
      {orderId && existingReservations.length > 0 && (
        <>
          <Alert
            message="Prenotazioni Esistenti"
            description={`Hai già prenotato ${existingReservations.reduce((sum, res) => sum + res.quantity_reserved, 0)} unità di questo prodotto.`}
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />
          <Card size="small" title="Stock Già Prenotati" style={{ marginBottom: 16 }}>
            <Table
              dataSource={existingReservations}
              rowKey="id"
              pagination={false}
              size="small"
              columns={[
                {
                  title: 'Codice Stock',
                  dataIndex: 'stock_code',
                  key: 'stock_code',
                },
                {
                  title: 'Quantità Prenotata',
                  dataIndex: 'quantity_reserved',
                  key: 'quantity_reserved',
                  render: (value: number) => <Text strong>{value.toLocaleString()}</Text>
                },
                {
                  title: 'Vendita Unitario',
                  dataIndex: 'unit_price',
                  key: 'unit_price',
                  render: (value: number) => `€ ${formatCurrency(value)}`
                },
                {
                  title: 'Totale',
                  dataIndex: 'total_price',
                  key: 'total_price',
                  render: (value: number) => <Text strong>€ {formatCurrency(value)}</Text>
                },
                {
                  title: 'Data Prenotazione',
                  dataIndex: 'reserved_at',
                  key: 'reserved_at',
                  render: (value: string) => new Date(value).toLocaleString()
                },
              ]}
            />
          </Card>
          <Divider>Stock Disponibili per Nuove Prenotazioni</Divider>
        </>
      )}
      
      <Table
        columns={columns}
        dataSource={stocksWithDesired}
        loading={loading}
        rowKey={(record, index) => `${record.stock_code || 'null'}_${index}`}
        pagination={false}
        scroll={{ x: 1000 }}
        size="small"
      />
      
      {renderFooter()}
    </Modal>
  );
};

export default ProductAvailabilityModal;